import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import commonDe from './js/i18n/de-DE/common.json';
import commonEn from './js/i18n/en-AU/common.json';
import filtersEn from './js/i18n/en-AU/filters.json';
import filtersDe from './js/i18n/de-DE/filters.json';
import countriesDe from './js/i18n/de-DE/countries.json';
import countriesEn from './js/i18n/en-AU/countries.json';
import LanguageDetector from 'i18next-browser-languagedetector';
import { browserClient } from './js/react-query';
import { useAuthentication } from './js/app-store/authentication/useAuthentication';
import * as Sentry from '@sentry/react';

// the translations
// (tip move them in a JSON file and import them,
// or even better, manage them separated from your code: https://react.i18next.com/guides/multiple-translation-files)

declare module 'i18next' {
    interface CustomTypeOptions {
        returnNull: false;
    }
}

export const LANGUAGE_EN_AU = 'en-AU';
export const LANGUAGE_DE_DE = 'de-DE';

const resources = {
    [LANGUAGE_EN_AU]: {
        common: commonEn,
        filters: filtersEn,
        countries: countriesEn
    },
    [LANGUAGE_DE_DE]: {
        common: commonDe,
        filters: filtersDe,
        countries: countriesDe
    }
};

export const AVAILABLE_LANGUAGES = [
    { id: LANGUAGE_EN_AU, key: 'english' },
    { id: LANGUAGE_DE_DE, key: 'german' }
];

export function parseLanguageCode(code: string) {
    if (code === 'de' || code.startsWith('de-')) {
        return LANGUAGE_DE_DE;
    }

    return LANGUAGE_EN_AU;
}

export function useFallbackLanguage() {
    const {
        spoc: { distributionTerritory }
    } = useAuthentication();

    if (distributionTerritory?.startsWith('DE_')) {
        return LANGUAGE_DE_DE;
    }

    return LANGUAGE_EN_AU;
}

i18n.use(initReactI18next)
    .use(LanguageDetector)
    .init({
        resources,
        interpolation: {
            escapeValue: false // react already safes from xss
        },

        fallbackLng: parseLanguageCode,
        returnNull: false,
        defaultNS: 'common',
        detection: {
            // order and from where user language should be detected
            order: [
                'querystring',
                'cookie',
                'localStorage',
                'sessionStorage',
                'navigator',
                'htmlTag',
                'path',
                'subdomain'
            ],

            // keys or params to lookup language from
            lookupQuerystring: 'lng',
            lookupCookie: 'i18next',
            lookupLocalStorage: 'i18nextLng',
            lookupSessionStorage: 'i18nextLng',
            lookupFromPathIndex: 0,
            lookupFromSubdomainIndex: 0,

            // cache user language on
            caches: ['localStorage', 'cookie'],
            excludeCacheFor: ['cimode'],
            cookieMinutes: 10,
            htmlTag: document.documentElement
        }
    })
    .then(data => {
        Sentry.setTag('clientLanguage', i18n.language);
    });

i18n.on('languageChanged', () => {
    Sentry.setTag('clientLanguage', i18n.language);
    void browserClient.invalidateQueries();
});

export default i18n;
